/**
 * InstallationsPage — Sprint 5.2B.
 *
 * Aba "Instalações" no NOC. Lista as sessões de instalação em campo
 * conduzidas pelos técnicos via o Portal do Técnico (/installer).
 *
 * Colunas: Cliente · QR · Template · Instalador · Status · Device
 *
 * Polling de 15s — reflete o avanço das instalações em tempo quase
 * real (técnico avança o wizard, device dá HB, auto-bind fecha).
 */
import { useCallback, useEffect, useState } from "react";
import { listInstallations } from "../lib/api.js";
import { formatRelative } from "../lib/format.js";
import { t } from "../i18n/index.js";

const POLL_MS = 15000;

export default function InstallationsPage() {
  const [rows, setRows] = useState([]);
  // NOC-INSTALLATIONS-CLEANUP — total bruto + rascunhos abandonados ocultos.
  const [meta, setMeta] = useState({ total: 0, hidden: 0, count: 0 });
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback((signal) => {
    listInstallations({ includeDrafts, signal })
      .then((data) => {
        setRows(data.sessions || []);
        setMeta({
          total: data.total ?? (data.sessions?.length || 0),
          hidden: data.hidden_stale_drafts ?? 0,
          count: data.count ?? (data.sessions?.length || 0),
        });
        setErr(null);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setErr(e?.message || String(e));
      })
      .finally(() => setLoading(false));
  }, [includeDrafts]);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h1 className="text-2xl font-bold tracking-tight text-noc-text">
            {t("installations.title")}
          </h1>
          <span className="text-[11px] text-noc-muted/70">
            {t("installations.autoHint")}
          </span>
        </div>
        {/* NOC-INSTALLATIONS-CLEANUP — contador de instalações reais + toggle
            de rascunhos antigos (sem apagar, só esconde). */}
        <div className="flex items-center gap-2.5">
          <span className="text-[11px] text-noc-muted">
            <strong className="text-noc-text">{meta.count}</strong>{" "}
            {meta.count === 1 ? "instalação" : "instalações"}
            {meta.hidden > 0 && !includeDrafts && (
              <span className="text-noc-faint">
                {" "}· {meta.hidden} rascunho{meta.hidden === 1 ? "" : "s"} antigo
                {meta.hidden === 1 ? "" : "s"} oculto{meta.hidden === 1 ? "" : "s"}
              </span>
            )}
            {includeDrafts && meta.total > meta.count && (
              <span className="text-noc-faint"> · {meta.total} no total</span>
            )}
          </span>
          {(meta.hidden > 0 || includeDrafts) && (
            <button
              type="button"
              onClick={() => setIncludeDrafts((v) => !v)}
              className="text-[11px] px-2 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text hover:border-noc-accent/40 whitespace-nowrap"
            >
              {includeDrafts ? "Ocultar rascunhos antigos" : "Mostrar rascunhos antigos"}
            </button>
          )}
        </div>
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
          {err}
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="text-sm text-noc-muted">{t("common.loading")}</div>
      )}

      {!loading && rows.length === 0 && !err && (
        <div className="text-sm font-mono text-[12px] text-noc-faint">
          {meta.hidden > 0 && !includeDrafts ? (
            <>
              Nenhuma instalação ativa. {meta.hidden} rascunho
              {meta.hidden === 1 ? "" : "s"} antigo{meta.hidden === 1 ? "" : "s"}{" "}
              oculto{meta.hidden === 1 ? "" : "s"} — clique “Mostrar rascunhos antigos”.
            </>
          ) : (
            t("installations.empty")
          )}
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto card-pad">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-noc-muted border-b border-noc-border">
                <Th>{t("installations.colCustomer")}</Th>
                <Th>{t("installations.colQr")}</Th>
                <Th>{t("installations.colTemplate")}</Th>
                <Th>{t("installations.colInstaller")}</Th>
                <Th>{t("installations.colStatus")}</Th>
                <Th>{t("installations.colDevice")}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-noc-border/40 hover:bg-noc-bg/30"
                >
                  <Td>
                    {s.customer_id ? (
                      <span className="font-mono text-noc-accent">
                        #{s.customer_id}
                      </span>
                    ) : (
                      <Dash />
                    )}
                  </Td>
                  <Td mono>{s.qr_serial || <Dash />}</Td>
                  <Td>
                    {s.template_id ? (
                      <span className="text-noc-accent">{s.template_id}</span>
                    ) : (
                      <Dash />
                    )}
                  </Td>
                  <Td>{s.installer || <Dash />}</Td>
                  <Td>
                    <InstallStatusBadge status={s.status} />
                  </Td>
                  <Td mono>{s.device_id || <Dash />}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] text-noc-muted/70 mt-2">
            {t("installations.countSuffix", { n: rows.length })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * InstallStatusBadge — Sprint 5.2B.
 * Mapeia status técnico → rótulo amigável + cor.
 */
function InstallStatusBadge({ status }) {
  const map = {
    draft: {
      label: t("installations.statusDraft"),
      cls: "border-noc-border text-noc-muted bg-noc-bg/40",
    },
    wifi_done: {
      label: t("installations.statusWifiDone"),
      cls: "border-noc-accent/30 text-noc-accent bg-noc-accent/10",
    },
    pending_device: {
      label: t("installations.statusPendingDevice"),
      cls: "border-noc-warn/30 text-noc-warn bg-noc-warn/10",
    },
    bound: {
      label: t("installations.statusBound"),
      cls: "border-noc-ok/30 text-noc-ok bg-noc-ok/10",
    },
    failed: {
      label: t("installations.statusFailed"),
      cls: "border-noc-err/30 text-noc-err bg-noc-err/10",
    },
  };
  const it = map[status] || {
    label: status || "?",
    cls: "border-noc-border text-noc-muted",
  };
  return (
    <span
      className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded border ${it.cls}`}
    >
      {it.label}
    </span>
  );
}

function Th({ children }) {
  return <th className="text-left font-medium py-1.5 px-2">{children}</th>;
}
function Td({ children, mono }) {
  return (
    <td className={`py-1.5 px-2 ${mono ? "font-mono" : ""}`}>{children}</td>
  );
}
function Dash() {
  return <span className="text-noc-muted/60 italic">—</span>;
}
